/*
 * Copyright (c) Akveo 2019. All Rights Reserved.
 * Licensed under the Single Application / Multi Application License.
 * See LICENSE_SINGLE_APP / LICENSE_MULTI_APP in the 'docs' folder for license information on type of purchased license.
 */

import { Component, OnDestroy, OnInit } from '@angular/core';
import { NbMenuItem, NbMenuService, NbSidebarService } from '@nebular/theme';

import { filter, map, startWith, takeUntil, tap } from 'rxjs/operators';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { AuthService, IdentityClaims } from '../../../@auth/auth.service';
import { BuyerService } from '../../../shared/services/buyer.service';
import { Router } from '@angular/router';
import {
  NotificationItem,
  NotificationService,
} from '../../../shared/services/notification.service';

@Component({
  selector: 'ngx-header',
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
})
export class HeaderComponent implements OnInit, OnDestroy {
  public userMenu$: Observable<NbMenuItem[]>;
  public totalNotifications$: Observable<string>;

  private destroy$: Subject<void> = new Subject<void>();
  private user: IdentityClaims;

  constructor(
    private sidebarService: NbSidebarService,
    private menuService: NbMenuService,
    private buyerService: BuyerService,
    private authService: AuthService,
    private notificationService: NotificationService,
    private router: Router
  ) {}

  ngOnInit() {
    this.user = this.authService.identityClaims;

    const notifications = this.notificationService
      .getAll()
      .pipe(startWith([] as NotificationItem[]));

    this.totalNotifications$ = notifications.pipe(
      map((notifications) =>
        notifications.reduce(
          (total, notification) => notification.total + total,
          0
        )
      ),
      map((total) => (total === 0 ? null : String(total)))
    );

    this.userMenu$ = combineLatest([
      this.impersonationMenuItems$,
      this.notificationService.getAll(),
      of(LOGOUT_MENU_ITEM),
    ]).pipe(
      map(([impersonationItems, notifications, logout]) =>
        [...impersonationItems, ...notifications, logout].filter(Boolean)
      )
    );
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  getTitle() {
    return window['__env'].appName ?? 'e-Livestock';
  }

  impersonationMenuItems$ = this.buyerService.getStockBuyerProfiles().pipe(
    takeUntil(this.destroy$),
    tap((profiles) => {
      let stockBuyerProfile = this.authService.getStockBuyerProfile();
      if (stockBuyerProfile) {
        stockBuyerProfile = profiles.find(
          (sbp) => sbp.number === stockBuyerProfile.number
        );
        if (stockBuyerProfile) {
          this.authService.setStockBuyerProfile(stockBuyerProfile);
        } else {
          this.router.navigateByUrl('/switch?stockBuyerNumber=');
        }
      }
    }),
    // If there's only one profile, it's the current user and they can't impersonate anyone
    // So don't emit out any impersonation menu items
    filter((profiles) => profiles.length > 1),
    map((profiles) => {
      const stockBuyerProfile = this.authService.getStockBuyerProfile();

      return profiles.map((item) => {
        return {
          title: item.name,
          link: '/switch',
          icon: 'person-outline',
          queryParams: {
            stockBuyerNumber: item.number,
          },
          data: item,
          ...((item.number === stockBuyerProfile?.number ||
            (!stockBuyerProfile && item.isDefault)) && {
            badge: {
              dotMode: true,
              status: 'primary',
            },
          }),
        } as NbMenuItem;
      });
    }),
    startWith([])
  );

  getUserName() {
    const stockBuyerProfile = this.authService.getStockBuyerProfile();

    return `${this.user?.name}${
      stockBuyerProfile?.isDefault === false
        ? ` (as ${stockBuyerProfile.name})`
        : ''
    }`;
  }

  toggleSidebar(): boolean {
    this.sidebarService.toggle(true, 'menu-sidebar');

    return false;
  }

  navigateHome() {
    this.menuService.navigateHome();
    return false;
  }
}

const LOGOUT_MENU_ITEM = {
  title: 'Log out',
  link: '../logout',
  icon: 'unlock-outline',
};
